import { ColDef, ValueGetterParams, ValueSetterParams } from "ag-grid-community";
import {
	MonthDayColumn,
	buildDateKey,
	createMonthDayColumnMeta,
	extractDayDdFromDateKey,
	formatDayColId,
	getDaysInMonth,
} from "src/utils/date/dateUtils";
import { withPinnedLeftColumn } from "src/utils/grid/gridStyleUtils";
import { formatDateKey } from "src/utils/string/stringUtil";
import { showWarning } from "src/utils/toast/toastUtil";
import {
	defaultTQC2100_R01DetailDto,
	TQC2100_R01CellValue,
	TQC2100_R01DetailDto,
	TQC2100_R01DetailPivotDto,
	TQC2100_R01InspectMasterDto,
} from "./Dto_TQC2100_R01";

export const headerNames_TQC2100_R01Detail: Record<string, string> = {
	rowIndex: "",
	inspItemNm: "점검내용",
};

/** 측정값은 빈 값 또는 부호와 소수점을 포함한 숫자만 허용한다. */
export function isValidMeasureInput(value: unknown): boolean {
	const normalized = String(value ?? "").trim();
	return normalized === "" || /^-?\d+(\.\d+)?$/.test(normalized);
}

export function normalizeMeasureValue(value: unknown): TQC2100_R01CellValue {
	return String(value ?? "").trim();
}

/** 선택한 연월의 1일부터 말일까지 동적 그리드 열 정보를 생성한다. */
export function getMonthDayColumns_TQC2100_R01(inspectDtYm: string): MonthDayColumn[] {
	const [year, month] = inspectDtYm.split("-");
	if (!year || !month) return [];
	const days = getDaysInMonth(inspectDtYm);
	return Array.from({ length: days }, (_, index) => {
		const day = index + 1;
		const dayDd = String(day).padStart(2, "0");
		const dayMeta = createMonthDayColumnMeta(day, inspectDtYm);
		return {
			...dayMeta,
			dayDd,
			colId: formatDayColId(day),
		};
	});
}

/** 단일 헤더(N일) 일자 컬럼 ColDef 생성 */
export function buildMonthDayColumnDefs(
	inspectDtYm: string,
): ColDef<TQC2100_R01DetailPivotDto>[] {
	const createDaySetter =
		(dayDd: string) =>
		(params: ValueSetterParams<TQC2100_R01DetailPivotDto, string>) => {
			if (!params.data) return false;
			if (!isValidMeasureInput(params.newValue)) {
				showWarning("숫자만 입력 가능합니다.");
				return false;
			}
			const nextValue = normalizeMeasureValue(params.newValue);
			const currentMap = params.data.dayValues;
			if ((currentMap?.[dayDd] ?? "") === nextValue) return false;
			if (!params.data.dayValues) {
				params.data.dayValues = {};
			}
			// paddedGridData는 행 객체를 shallow copy하므로 dayValues 객체 참조를 유지한 채 in-place 갱신해야 store와 동기화된다.
			params.data.dayValues[dayDd] = nextValue;
			return true;
		};

	return getMonthDayColumns_TQC2100_R01(inspectDtYm).flatMap((column) => {
		if (!column.colId || !column.dayDd) return [];
		return [
			{
				headerName: column.headerName, colId: column.colId, field: column.colId as keyof TQC2100_R01DetailPivotDto & string, width: 75,
				editable: true,
				headerClass: column.headerClass,
				cellStyle: { textAlign: "center" },
				valueGetter: (params: ValueGetterParams<TQC2100_R01DetailPivotDto>) => params.data?.dayValues?.[column.dayDd!] ?? "",
				valueSetter: createDaySetter(column.dayDd),
			},
		];
	});
}

function createEmptyDayValues(inspectDtYm: string): Record<string, string> {
	return Object.fromEntries(
		getMonthDayColumns_TQC2100_R01(inspectDtYm).map((column) => [column.dayDd!, ""]),
	);
}

/** 서버의 항목·일자별 상세를 점검내용 행과 날짜 열 구조로 변환한다. */
export function pivotDetailToGridRows(
	flatList: TQC2100_R01DetailDto[],
	masterList: TQC2100_R01InspectMasterDto[],
	inspectDtYm: string,
): TQC2100_R01DetailPivotDto[] {
	// 저장 상세에 항목명이 없더라도 검사기준 마스터를 기준으로 빈 행을 유지한다.
	const fallbackMasters = Array.from(
		new Map(
			flatList.map((item) => [
				item.inspItemCd,
				{
					inspItemGroup: item.inspItemGroup || "MS",
					inspType: item.inspType || "FPI",
					inspItemCd: item.inspItemCd,
					inspItemNm: item.inspItemNm,
				},
			]),
		).values(),
	);
	const source = masterList.length > 0 ? masterList : fallbackMasters;

	return source.map((master, index) => {
		const dayValues = createEmptyDayValues(inspectDtYm);
		const dayMeta: Record<string, { itemNo?: string }> = {};
		flatList
			.filter((item) => item.inspItemCd === master.inspItemCd)
			.forEach((item) => {
				const dateKey = formatDateKey(item.inspectDt);
				if (!dateKey.startsWith(inspectDtYm)) return;
				const dayDd = extractDayDdFromDateKey(dateKey);
				dayValues[dayDd] = normalizeMeasureValue(item.measureValue);
				if (item.itemNo) {
					dayMeta[dayDd] = { itemNo: item.itemNo };
				}
			});
		return {
			...master,
			valueIndex: index + 1,
			dayValues,
			dayMeta,
			isModified: false,
		};
	});
}

type DetailBase = Pick<
	TQC2100_R01DetailDto,
	| "inspectNo"
	| "compCd"
	| "inspectorEmp"
	| "inspectorEmpNm"
	| "approvorEmpno"
	| "approvorEmpNm"
	| "headerRemark"
>;

/** 피벗 그리드에서 값이 입력된 셀만 서버 저장용 상세 목록으로 펼친다. */
export function flattenGridRowsToDetail(
	pivotList: TQC2100_R01DetailPivotDto[],
	base: DetailBase,
	inspectDtYm: string,
): TQC2100_R01DetailDto[] {
	const result: TQC2100_R01DetailDto[] = [];
	const monthDayColumns = getMonthDayColumns_TQC2100_R01(inspectDtYm);
	pivotList.forEach((row) => {
		monthDayColumns.forEach((column) => {
			const dayDd = column.dayDd;
			if (!dayDd) return;
			const value = row.dayValues[dayDd];
			if (normalizeMeasureValue(value) === "") return;
			const dateKey = buildDateKey(inspectDtYm, dayDd);
			result.push({
				...defaultTQC2100_R01DetailDto,
				...base,
				inspItemGroup: row.inspItemGroup,
				inspType: row.inspType,
				inspItemCd: row.inspItemCd,
				inspItemNm: row.inspItemNm,
				// LocalDate는 시간대 변환 없이 날짜 문자열 그대로 전송해야 일자가 밀리지 않는다.
				inspectDt: dateKey,
				measureValue: normalizeMeasureValue(value),
				itemNo: row.dayMeta[dayDd]?.itemNo || "",
				inserted: !row.dayMeta[dayDd]?.itemNo,
			});
		});
	});
	return result;
}
